"use client";

import React from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { BaseCoverageId } from "@/config/coverages";
import { OfferResponse } from "@/types/simpego-api";
import { buildRows } from "@/utils/coverageRows";
import { isPetYoungerThanAgeBand } from "@/utils/coveragePayload";
import { RowValue, RowLabel, SectionInfo } from "./CoverageRowParts";

interface OfferCoverageListProps {
  /** The offer the price was calculated for; `null` while it is still loading. */
  offer: OfferResponse | null;
  coverageSelections: Partial<Record<BaseCoverageId, boolean>>;
  /** Coverages the underwriting rejected: never listed as insured. */
  declinedCoverages?: Partial<Record<BaseCoverageId, string>>;
  petBirthDate?: Date | null;
  /** "left": the values start at one shared left edge (summary page); "right": right-aligned, marks after the amount (cart). */
  valueAlign?: "left" | "right";
}

/** The chosen package as one column of the comparison table: same sections, labels and values. */
const OfferCoverageList: React.FC<OfferCoverageListProps> = ({ offer, coverageSelections, declinedCoverages = {}, petBirthDate, valueAlign = "right" }) => {
  const { t } = useTranslation();
  const isYoung = isPetYoungerThanAgeBand(petBirthDate ?? null);
  const rows = React.useMemo(() => {
    const selections = { ...coverageSelections };
    for (const id of Object.keys(declinedCoverages) as BaseCoverageId[]) selections[id] = false;
    return buildRows(selections, isYoung);
  }, [coverageSelections, declinedCoverages, isYoung]);

  if (!offer) {
    return <Loader2 className="mx-auto h-6 w-6 animate-spin text-primary" aria-label={t("common.loading")} />;
  }

  return (
    <div>
      {rows.map((row, index) =>
        row.kind === "section" ? (
          <div key={index} className={cn("flex items-center gap-2 border-b border-border px-3 py-1.5 text-sm", row.sub ? "font-medium" : "bg-muted/30 font-semibold")}>
            <span>{t(row.labelKey)}</span>
            <SectionInfo row={row} t={t} />
          </div>
        ) : (
          <div key={index} className={cn("flex flex-wrap items-center justify-between gap-x-3 gap-y-1 border-b border-border px-3 py-1.5 last:border-b-0", row.nested && "pl-8")}>
            <span className="min-w-min flex-1 text-sm">
              <RowLabel row={row} t={t} />
            </span>
            {valueAlign === "left" ? (
              <span className="w-[45%] shrink-0 text-left">
                <RowValue row={row} offer={offer} t={t} loading={false} alignLeft />
              </span>
            ) : (
              // Right-aligned; the check mark sits after the amount so the marks line up in one column.
              <span className="ml-auto min-w-0 max-w-full text-right">
                <RowValue row={row} offer={offer} t={t} loading={false} iconEnd />
              </span>
            )}
          </div>
        )
      )}
    </div>
  );
};

export default OfferCoverageList;
